import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { AdminSettingsService } from '@core/services/admin-settings.service';
import { GeneralSettings } from '@models/admin-settings';
// import { error } from 'console';

@Component({
  selector: 'app-settings-general-info-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './app-settings-general-info-form.component.html',
  styleUrl: './app-settings-general-info-form.component.css'
})
export class AppSettingsGeneralInfoFormComponent implements OnInit, OnDestroy {

  // Form and loading state
  generalForm!: FormGroup;
  isLoading = false;
  isSaving = false;
  isEditMode = false; // Add edit mode state

  // Component Cleanup
  private destroy$ = new Subject<void>();

  // Language options for the dropdown
  languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' }
  ];

  // SUccess/Error messages
  successMessage: string = '';
  errorMessage: string = '';

  constructor(
    private fb: FormBuilder,
    private adminSettingsService: AdminSettingsService
  ) {
    this.createForm();
  }

  ngOnInit(): void {
    this.loadSettings();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* Create the form with validation */
  private createForm(): void {
    this.generalForm = this.fb.group({
      applicationName: [{value: '', disabled: true}, Validators.required],
      defaultLanguage: [{value: 'en', disabled: true}, Validators.required],
      applicationDescription: [{value: '', disabled: true}, [Validators.required, Validators.minLength(10)]],
      restrictUserRegistration: [{value: false, disabled: true}],
      maintenanceMode: [{value: false, disabled: true}]
    });
  }

  /* Load Settings from the Service */
  private loadSettings(): void {
    this.isLoading = true;
    this.clearMessages();

    this.adminSettingsService.getGeneralSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.populateForm(response.generalSettings);
          } else {
            this.errorMessage = response.message || 'Failed to load settings';
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading settings:', error);
          this.errorMessage = 'Failed to load settings. Please try again.';
          this.isLoading = false;
        }
      });
  }

  /* Populate the form with loaded data */
  private populateForm(settings: GeneralSettings): void {
    this.generalForm.patchValue({
      applicationName: settings.applicationName,
      defaultLanguage: settings.defaultLanguage,
      applicationDescription: settings.applicationDescription,
      restrictUserRegistration: settings.restrictUserRegistration,
      maintenanceMode: settings.maintenanceMode
    });
  }

  /**
   * Save form data
   */
  onSubmit(): void {
    if (this.generalForm.valid && this.generalForm.dirty) {
      this.generalForm.disable();
      this.isSaving = true;
      this.clearMessages();

      const formData: GeneralSettings = this.generalForm.value;

      this.adminSettingsService.saveGeneralSettings(formData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.successMessage = response.message || 'Settings saved successfully!';
              this.isEditMode = false; // Exit edit mode on successful save
              // Auto-clear success message after 3 seconds
              setTimeout(() => this.successMessage = '', 3000);
            } else {
              this.errorMessage = response.message || 'Failed to save settings';
            }
            this.isSaving = false;
            this.generalForm.enable();
          },
          error: (error) => {
            console.error('Error saving settings:', error);
            this.errorMessage = 'Failed to save settings. Please try again.';
            this.isSaving = false;
            this.generalForm.enable();
          }
        });
    } else if (!this.generalForm.dirty) {
    this.successMessage = 'No changes to save';
    setTimeout(() => this.successMessage = '', 2000);
    }
   else {
      this.markFormGroupTouched();
    }
  }

   /**
   * Mark all form fields as touched to show validation errors
   */
  private markFormGroupTouched(): void {
    Object.keys(this.generalForm.controls).forEach(key => {
      const control = this.generalForm.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * Clear success/error messages
   */
  private clearMessages(): void {
    this.successMessage = '';
    this.errorMessage = '';
  }

  /**
   * Get form control for easier template access
   */
  getControl(controlName: string) {
    return this.generalForm.get(controlName);
  }

  /**
   * Toggle edit mode
   */
  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
    
    if (this.isEditMode) {
      // Enable all form controls when entering edit mode
      this.generalForm.enable();
    } else {
      // Disable all form controls and reset to loaded values when exiting edit mode
      this.generalForm.disable();
      this.loadSettings();
    }
    
    this.clearMessages();
  }

  /**
   * Reset form to initial/loaded values
   */
  resetForm(): void {
    this.generalForm.reset();
    this.loadSettings(); // Reload from service
    this.clearMessages();
  }

  /**
   * Check if a form control has an error and is touched
   */
  hasError(controlName: string, errorType: string): boolean {
    const control = this.getControl(controlName);
    return !!(control && control.hasError(errorType) && control.touched);
  }

}